import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import AIBanner from '@components/AIBanner';
import TableModal from '@components/TableModal';
import FilterPanel from '@components/FilterPanel';
import InspectionAssistantModal from '@features/InspectionAssistantModal';
import { translate } from '@utils/i18n';
import api from '@utils/axios';
import { Button } from '@ui';
import { DateRangeFilter } from '@ui/molecules/Filter/DateRangeFilter';
import { hasGlobalTerritoriesApplied } from '@helpers/territories';
import { inspectionsPath, workOrdersPath, inspectionDashboardAnalyticReportsPath } from 'routes';
import { loadCacheFromLocalStorage, saveCacheToLocalStorage } from '../../../helpers/localStorage';
import {
  WORK_ORDER_DASHBOARD_PRESETS,
  DEFAULT_WORK_ORDER_DASHBOARD_PRESET_ID,
  CUSTOM_WORK_ORDER_DASHBOARD_PRESET_ID,
  ALL_TIME_WORK_ORDER_DASHBOARD_PRESET_ID,
  getPresetById
} from './utils/workOrderDashboardDatePresets.ts';
import { WidgetFilterMenu } from './nested/WidgetFilterMenu/index.ts';
import { patchWorkOrderDashboardPreset } from './services/workOrderDashboardPreference.ts';
import { fetchWorkOrderSummary } from './services/workOrderSummary.ts';
import './Home.scss';

import ServiceVisitsDisabled from './nested/ServiceVisitsDisabled';
import Invoices from './nested/Invoices';
import Proposals from './nested/Proposals';
import ProposalsDisabled from './nested/ProposalsDisabled';
import Payments from './nested/Payments';
import PaymentsDisabled from './nested/PaymentsDisabled';
import Deficiencies from './nested/Deficiencies';
import Onboarding from './nested/Onboarding';
import useDataTableContext from './context/useDataTableContext';
import getModalInfo from './utils/getModalInfo';
import DashboardSettings from './nested/DashboardSettings';
import InspectionAssistantWidget from './nested/InspectionAssistantWidget';
import StatusCounterWidget from './nested/StatusCounterWidget';
import { DashboardEntityType } from './context/constants';
import { HomeDataTableProvider } from './context/HomeDataTableContext';
import { BaseOtherStatus } from './nested/StatusCounterWidget/constants';
import FilterTypes from '../../../constants/FilterTypes.ts';

const namespace = 'features.dashboards.home';
const DASHBOARD_FILTER_CACHE_KEY = 'dashboard/home/filters';

function HomeDashboard({
  dashboardData,
  territoryFilter,
  onTerritoryFilterChange,
  workOrderPresetId,
  workOrderCustomRange,
  onWorkOrderPresetChange,
  onWorkOrderCustomApply,
  workOrderRefreshing,
  workOrderCountsLoading
}) {
  const {
    state: {
      isModalOpen,
      statusSelected,
      inspectionStatuses,
      workOrderStatuses,
      statusesSelection,
      entityType,
      columnsStructureData,
      tableData,
      isToday,
      loading: loadingModalData
    },
    actions: { closeModal, loadInspections, loadWorkOrders, loadHighPriorities }
  } = useDataTableContext();

  const globalTerritoriesApplied = hasGlobalTerritoriesApplied();

  const onResetFilters = useCallback(() => {
    onTerritoryFilterChange([]);
  }, [onTerritoryFilterChange]);

  const filtersList = useMemo(() => {
    const filters = [];

    if (!globalTerritoriesApplied) {
      filters.push({
        field: 'territory',
        locale: 'territory',
        type: FilterTypes.GenericMultiSelect,
        active: true,
        value: territoryFilter,
        withUnassigned: true,
        onChange: onTerritoryFilterChange
      });
    }

    return filters;
  }, [territoryFilter, onTerritoryFilterChange, globalTerritoriesApplied]);

  const {
    userName,
    workOrders,
    invoices,
    proposals,
    deficiencies,
    payments,
    currency,
    onboarding,
    month,
    topCounters,
    permissions,
    inspectionAssistantWidget
  } = dashboardData;

  const greeting = useMemo(() => {
    const hour = new Date().getHours();

    if (hour < 12) return translate('greeting.good_morning', { namespace });
    if (hour < 17) return translate('greeting.good_afternoon', { namespace });
    return translate('greeting.good_evening', { namespace });
  }, []);

  // Effective date range: use custom dates when preset is custom, else compute from preset.
  const workOrderDateRange = useMemo(() => {
    if (
      workOrderPresetId === CUSTOM_WORK_ORDER_DASHBOARD_PRESET_ID &&
      workOrderCustomRange?.start &&
      workOrderCustomRange?.end
    ) {
      return workOrderCustomRange;
    }
    return getPresetById(workOrderPresetId).compute();
  }, [workOrderPresetId, workOrderCustomRange]);

  const appliedWorkOrderPresetIdRef = useRef(workOrderPresetId);
  const isCustomDraft =
    workOrderPresetId === CUSTOM_WORK_ORDER_DASHBOARD_PRESET_ID &&
    !(workOrderCustomRange?.start && workOrderCustomRange?.end);

  useEffect(() => {
    if (!isCustomDraft) {
      appliedWorkOrderPresetIdRef.current = workOrderPresetId;
    }
  }, [workOrderPresetId, isCustomDraft]);

  // Label is a direct lookup — no fragile getTime matching against rolling ranges.
  // While Custom is in draft (no complete range), keep showing the last applied preset label.
  const workOrderTimeFrameLabel = useMemo(() => {
    const id = isCustomDraft ? appliedWorkOrderPresetIdRef.current : workOrderPresetId;
    return getPresetById(id)?.label;
  }, [workOrderPresetId, isCustomDraft, workOrderCustomRange]);

  // Fixed preset buttons use onPresetSelect (explicit id). Custom enters draft mode immediately
  // so the highlight updates and calendar onChange partial ranges are accepted.
  const handleWorkOrderPresetSelect = useCallback(
    (presetId) => {
      if (presetId === CUSTOM_WORK_ORDER_DASHBOARD_PRESET_ID) {
        // Clear bounds (resetOnCustomRange) and enter custom without PATCH until both dates exist.
        onWorkOrderCustomApply({ start: null, end: null });
        return;
      }
      onWorkOrderPresetChange(presetId);
    },
    [onWorkOrderPresetChange, onWorkOrderCustomApply]
  );

  // Calendar edits (and legacy onChange-only preset paths) → custom apply / date match.
  // Accept partial ranges ({start, end:null}) so Kendo mid-selection does not snap back.
  const handleDateRangeFilterChange = useCallback(
    (dateRange) => {
      // DateRangeFilter Reset emits {null,null}. Restore the plan default (Last 6 months)
      // — do not treat empty bounds as Custom draft (IP-15198). All-time uses onPresetSelect only.
      if (dateRange.start == null && dateRange.end == null) {
        onWorkOrderPresetChange(DEFAULT_WORK_ORDER_DASHBOARD_PRESET_ID);
        return;
      }

      // Prefer matching a fixed preset before treating the range as custom.
      const matched = WORK_ORDER_DASHBOARD_PRESETS.find((p) => {
        if (p.id === CUSTOM_WORK_ORDER_DASHBOARD_PRESET_ID || p.id === ALL_TIME_WORK_ORDER_DASHBOARD_PRESET_ID) {
          return false; // all-time via onPresetSelect only
        }
        const c = p.compute();
        if (!c.start || !c.end || !dateRange.start || !dateRange.end) return false;
        // Local ISO-date (YYYY-MM-DD) comparison guards against UTC-offset drift in rolling presets.
        return (
          DateTime.fromJSDate(c.start).toISODate() === DateTime.fromJSDate(dateRange.start).toISODate() &&
          DateTime.fromJSDate(c.end).toISODate() === DateTime.fromJSDate(dateRange.end).toISODate()
        );
      });
      if (matched) {
        onWorkOrderPresetChange(matched.id);
        return;
      }

      // Any calendar edit that is not a fixed preset (including partial start-only) is Custom.
      onWorkOrderCustomApply({ start: dateRange.start, end: dateRange.end });
    },
    [onWorkOrderPresetChange, onWorkOrderCustomApply]
  );

  const statusInfo = useMemo(() => {
    switch (entityType) {
      case DashboardEntityType.Inspections:
        return [...inspectionStatuses, BaseOtherStatus].find((status) => status.statusCode === statusSelected);
      case DashboardEntityType.WorkOrders:
        return [...workOrderStatuses, BaseOtherStatus].find((status) => status.statusCode === statusSelected);
      case DashboardEntityType.Deficiencies:
        return { name: statusSelected.replace(/_/g, ' ') };
      default:
        return {};
    }
  }, [workOrderStatuses, inspectionStatuses, entityType, statusSelected]);

  const onOpenSidebar = () => {
    const userSideBar = document.getElementById('user-panel');
    if (!userSideBar.className.includes('--active')) {
      userSideBar.className += ' --active';
    }
  };

  const rightActionLink = useMemo(() => {
    switch (entityType) {
      case DashboardEntityType.Inspections:
        return inspectionDashboardAnalyticReportsPath();
      case DashboardEntityType.WorkOrders:
        return null;
      case DashboardEntityType.Invoices:
      case DashboardEntityType.PaidInvoices:
        return invoices.expandLink;
      case DashboardEntityType.Proposals:
        return proposals.expandLink;
      case DashboardEntityType.Deficiencies:
        return deficiencies.expandLink;
      case DashboardEntityType.Payments:
        return payments.expandLink;
      default:
        return '';
    }
  }, [entityType]);

  const modalInfo = getModalInfo({
    entityType,
    status: statusInfo,
    data: tableData,
    month,
    rangeFromToday: isToday,
    rightActionLink
  });

  return (
    <>
      <div className="dashboard__accent">
        <svg role="img" x="0" y="0" viewBox="0 0 64 64">
          <linearGradient id="dashboard-accent__grad" gradientTransform="rotate(90)">
            <stop offset="0%" className="grad__stop--01" />
            <stop offset="66%" className="grad__stop--02" />
          </linearGradient>
          <polygon points="64,0 0,64 0,0" fill="url(#dashboard-accent__grad)" />
        </svg>
      </div>
      <div className="dashboard__accent--cutoff">
        <svg role="img" x="0" y="0" viewBox="0 0 64 64">
          <linearGradient id="dashboard-accent__grad" gradientTransform="rotate(90)">
            <stop offset="0%" className="grad__stop--01" />
            <stop offset="66%" className="grad__stop--02" />
          </linearGradient>
          <polygon points="64,0 0,64 0,0" fill="url(#dashboard-accent__grad)" />
        </svg>
      </div>
      <div className="dashboard__greet workspace__header--alt--sticky">
        <div className="dashboardGreeting">
          <h1>
            {greeting}
            {userName ? `, ${userName.split(' ')[0]}` : '!'}
          </h1>
        </div>
        <DashboardSettings permissions={{ ...permissions, service: workOrders.hasService }} />
      </div>

      {onboarding.isEnabled && (
        <>
          <div className="workspace__walkthrough">
            <Onboarding sections={onboarding.sections} skipOnboardingLink={onboarding.skipOnboardingLink} />
          </div>

          <hr className="workspace__split" />
        </>
      )}

      <div className="dashboard__contain">
        <AIBanner />
        <div className="dashboard__widgets">
          <div
            className="dashboardGeneralMetrics"
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <Button className="qmb-widget--counter" onClick={() => loadInspections({ isToday: true })}>
                <span className="widget__value">{topCounters.inspections}</span>
                <span className="widget__label">{translate('headers_metrics.inspections_today', { namespace })}</span>
              </Button>
              {workOrders.hasService && (
                <Button
                  className="qmb-widget--counter"
                  onClick={() => loadWorkOrders({ isToday: true, allVisits: true })}>
                  <span className="widget__value">{topCounters.workOrders}</span>
                  <span className="widget__label">
                    {translate('headers_metrics.service_visits_today', { namespace })}
                  </span>
                </Button>
              )}
              <Button className="qmb-widget--counter" onClick={loadHighPriorities}>
                <span className="widget__value">{topCounters.highPriority}</span>
                <span className="widget__label">{translate('headers_metrics.high_priority_items', { namespace })}</span>
              </Button>
            </div>
            {permissions?.territories && (
              <FilterPanel onResetFilters={onResetFilters} filters={filtersList} toggleDisabled />
            )}
          </div>
          <StatusCounterWidget
            widgetTitle={translate('inspections', { namespace: 'generic' })}
            statusesInfo={inspectionStatuses}
            statusSelection={statusesSelection.inspections}
            onShowStatusTable={loadInspections}
            month={month}
            chartsLink={inspectionDashboardAnalyticReportsPath()}
            chartsTitle={translate('inspections.expand_title', { namespace })}
            viewAllLink={inspectionsPath()}
          />
          {workOrders.hasService && (
            <StatusCounterWidget
              widgetTitle={translate('service', { namespace: 'generic' })}
              statusesInfo={workOrderStatuses}
              statusSelection={statusesSelection.workOrders}
              onShowStatusTable={loadWorkOrders}
              month={month}
              viewAllLink={workOrdersPath()}
              timeFrameLabel={workOrderTimeFrameLabel}
              loading={workOrderCountsLoading}
              filterMenu={
                <WidgetFilterMenu
                  title={translate('service_filter.title', { namespace })}
                  triggerTitle={translate('service_filter.title', { namespace })}
                  loading={workOrderRefreshing}>
                  <DateRangeFilter
                    field="workOrderVisitDate"
                    label={translate('service_filter.date_label', { namespace })}
                    value={workOrderCustomRange ?? workOrderDateRange}
                    onChange={handleDateRangeFilterChange}
                    onPresetSelect={handleWorkOrderPresetSelect}
                    presets={WORK_ORDER_DASHBOARD_PRESETS}
                    selectedPresetId={workOrderPresetId}
                    resetOnCustomRange
                    showReset={workOrderPresetId !== DEFAULT_WORK_ORDER_DASHBOARD_PRESET_ID}
                  />
                </WidgetFilterMenu>
              }
            />
          )}
          {!workOrders.hasService && <ServiceVisitsDisabled supportEmail={workOrders.supportEmail} />}
          {invoices && (
            <Invoices
              timeFrame={invoices.timeFrame}
              amountPaid={invoices.amountPaid}
              amountPaidCount={invoices.amountPaidCount}
              amountDue={invoices.amountDue}
              amountDueCount={invoices.amountDueCount}
              amountTotal={invoices.amountTotal}
              amountTotalCount={invoices.amountTotalCount}
              amountOverDueCount={invoices.amountOverDueCount}
              amountOverDue={invoices.amountOverDue}
              currency={currency}
              expandLink={invoices.expandLink}
              listingLink={invoices.listingLink}
            />
          )}
          {proposals.hasCorrections && (
            <Proposals
              timeFrame={proposals.timeFrame}
              accepted={proposals.accepted}
              sent={proposals.sent}
              declined={proposals.declined}
              created={proposals.created}
              expandLink={proposals.expandLink}
              listingLink={proposals.listingLink}
            />
          )}
          {!proposals.hasCorrections && <ProposalsDisabled supportEmail={proposals.supportEmail} />}
          <Deficiencies
            timeFrame={deficiencies.timeFrame}
            critical={deficiencies.critical}
            uncategorized={deficiencies.uncategorized}
            impairment={deficiencies.impairment}
            nonCritical={deficiencies.nonCritical}
            expandLink={deficiencies.expandLink}
            listingLink={deficiencies.listingLink}
          />
          {inspectionAssistantWidget && (
            <InspectionAssistantWidget
              inspectionCount={inspectionAssistantWidget.inspectionCount}
              escaped={inspectionAssistantWidget.escaped}
              resolved={inspectionAssistantWidget.resolved}
              totalIssues={inspectionAssistantWidget.totalIssues}
              expandLink={inspectionAssistantWidget.expandLink}
            />
          )}
          {payments.hasPayments && (
            <Payments
              timeFrame={payments.timeFrame}
              totalCount={payments.totalCount}
              totalAmount={payments.totalAmount}
              averageAmount={payments.averageAmount}
              expandLink={payments.expandLink}
            />
          )}
          {!payments.hasPayments && <PaymentsDisabled link={payments.expandLink} />}
        </div>
      </div>
      <TableModal
        isOpen={isModalOpen}
        loading={loadingModalData}
        title={modalInfo.title}
        subTitleView={modalInfo.subTitleView}
        data={tableData}
        columns={columnsStructureData}
        onClose={closeModal}
        timeFrame={modalInfo.timeFrame}
        className="qmb-dialog--modal"
        rightActionButton={modalInfo.rightActionButton}
      />
      <InspectionAssistantModal />
    </>
  );
}

HomeDashboard.defaultProps = {
  dashboardData: {},
  territoryFilter: [],
  onTerritoryFilterChange: () => {},
  workOrderPresetId: DEFAULT_WORK_ORDER_DASHBOARD_PRESET_ID,
  workOrderCustomRange: null,
  onWorkOrderPresetChange: () => {},
  onWorkOrderCustomApply: () => {},
  workOrderRefreshing: false,
  workOrderCountsLoading: false
};

HomeDashboard.propTypes = {
  dashboardData: PropTypes.object.isRequired,
  territoryFilter: PropTypes.array,
  onTerritoryFilterChange: PropTypes.func.isRequired,
  /** Active preset ID for the Service widget date filter. */
  workOrderPresetId: PropTypes.string,
  /** Custom date range when preset is 'custom'; null otherwise. */
  workOrderCustomRange: PropTypes.shape({ start: PropTypes.instanceOf(Date), end: PropTypes.instanceOf(Date) }),
  /** Callback when the user selects a new fixed/all-time preset. */
  onWorkOrderPresetChange: PropTypes.func,
  /** Callback when the user applies a custom calendar date range. */
  onWorkOrderCustomApply: PropTypes.func,
  /** True while a soft refetch of work-order counts is in-flight; replaces Service filter icon with Loader. */
  workOrderRefreshing: PropTypes.bool,
  /** True while the first work-order summary request is in-flight; drives Service StatusCounterWidget loading. */
  workOrderCountsLoading: PropTypes.bool
};

// will be replaced with complete cache handler when new filters appear
const loadTerritoryFilter = () => {
  const cached = loadCacheFromLocalStorage(DASHBOARD_FILTER_CACHE_KEY);
  return cached?.territoryFilter || [];
};

const saveTerritoryFilter = (territoryFilter) => {
  saveCacheToLocalStorage(DASHBOARD_FILTER_CACHE_KEY, { territoryFilter });
};

function resolveBootstrapPreference(workOrderDatePreset, workOrderDateStart, workOrderDateEnd) {
  const presetId = getPresetById(workOrderDatePreset || DEFAULT_WORK_ORDER_DASHBOARD_PRESET_ID).id;
  let customDateRange = null;

  if (presetId === CUSTOM_WORK_ORDER_DASHBOARD_PRESET_ID && workOrderDateStart && workOrderDateEnd) {
    customDateRange = {
      start: DateTime.fromISO(workOrderDateStart).toJSDate(),
      end: DateTime.fromISO(workOrderDateEnd).toJSDate()
    };
  }

  return { presetId, customDateRange };
}

function HomeDashboardWrapper({ workOrderDatePreset, workOrderDateStart, workOrderDateEnd }) {
  const bootstrapPreference = resolveBootstrapPreference(workOrderDatePreset, workOrderDateStart, workOrderDateEnd);

  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [workOrderSummary, setWorkOrderSummary] = useState(null);
  const [isWorkOrderSummaryLoading, setIsWorkOrderSummaryLoading] = useState(false);
  const [territoryFilter, setTerritoryFilterState] = useState(loadTerritoryFilter);
  const [workOrderPresetId, setWorkOrderPresetId] = useState(bootstrapPreference.presetId);
  // Stores user-picked custom dates; only meaningful when workOrderPresetId === custom.
  const [customDateRange, setCustomDateRange] = useState(bootstrapPreference.customDateRange);

  // Tracks the latest resolved dashboard data so fetchDashboardData can detect initial vs soft load
  // without depending on dashboardData state (which would form a stale-closure in the useCallback).
  const dashboardDataRef = useRef(null);
  // Tracks the most recently issued aggregate request so a slower, older request cannot overwrite the dashboard.
  const latestRequestIdRef = useRef(0);
  // Tracks the latest resolved summary so fetchWorkOrderSummaryData can detect initial vs soft load.
  const workOrderSummaryRef = useRef(null);
  // Tracks the most recently issued summary request so out-of-order responses are discarded.
  const latestSummaryRequestIdRef = useRef(0);
  // Sequence + serialize preference PATCHes so a late failure/success cannot overwrite a newer selection.
  const presetMutationSeqRef = useRef(0);
  const presetPatchChainRef = useRef(Promise.resolve());
  // Last preference known to be persisted on the server; used for rollback instead of optimistic prior UI.
  const persistedPreferenceRef = useRef({
    presetId: bootstrapPreference.presetId,
    customDateRange: bootstrapPreference.customDateRange
  });

  useEffect(() => {
    saveTerritoryFilter(territoryFilter);
  }, [territoryFilter]);

  const setTerritoryFilter = useCallback((newTerritoryFilter) => {
    setTerritoryFilterState(newTerritoryFilter);
  }, []);

  // Must stay above the loading early-return so hook order is stable across renders.
  // For custom, use the stored calendar dates; for fixed presets, compute from preset.
  const workOrderDateRange = useMemo(() => {
    if (workOrderPresetId === CUSTOM_WORK_ORDER_DASHBOARD_PRESET_ID && customDateRange?.start && customDateRange?.end) {
      return customDateRange;
    }
    return getPresetById(workOrderPresetId).compute();
  }, [workOrderPresetId, customDateRange]);

  const fetchDashboardData = useCallback(async (territoryIds) => {
    const isInitialLoad = dashboardDataRef.current === null;
    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;
    try {
      if (isInitialLoad) {
        setLoading(true);
      }
      const params = {};

      if (territoryIds && territoryIds.length > 0) {
        params.territory_ids = territoryIds.map((t) => t.value || t);
      }

      const response = await api.get('/api/internal/dashboard', { params });
      if (requestId !== latestRequestIdRef.current) return;

      setDashboardData(response.data);
      dashboardDataRef.current = response.data;
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      if (isInitialLoad && requestId === latestRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const fetchWorkOrderSummaryData = useCallback(async (territoryIds, presetId, effectiveDateRange) => {
    const requestId = latestSummaryRequestIdRef.current + 1;
    latestSummaryRequestIdRef.current = requestId;
    try {
      setIsWorkOrderSummaryLoading(true);

      const response = await fetchWorkOrderSummary({
        territoryIds,
        presetId,
        dateRange: effectiveDateRange
      });
      if (requestId !== latestSummaryRequestIdRef.current) return;

      setWorkOrderSummary(response);
      workOrderSummaryRef.current = response;
    } catch (error) {
      console.error('Failed to fetch work order summary:', error);
      if (requestId !== latestSummaryRequestIdRef.current) return;

      toast.error(translate('service_filter.load_error', { namespace }));
      if (workOrderSummaryRef.current === null) {
        const emptySummary = { statuses: [] };
        setWorkOrderSummary(emptySummary);
        workOrderSummaryRef.current = emptySummary;
      }
    } finally {
      if (requestId === latestSummaryRequestIdRef.current) {
        setIsWorkOrderSummaryLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchDashboardData(territoryFilter);
  }, [fetchDashboardData, territoryFilter]);

  useEffect(() => {
    // Custom draft (clicked Custom or picked only start) must not refetch — empty params
    // would hit the backend default month window and fight the controlled calendar value.
    if (
      workOrderPresetId === CUSTOM_WORK_ORDER_DASHBOARD_PRESET_ID &&
      !(customDateRange?.start && customDateRange?.end)
    ) {
      return;
    }
    fetchWorkOrderSummaryData(territoryFilter, workOrderPresetId, workOrderDateRange);
  }, [fetchWorkOrderSummaryData, territoryFilter, workOrderPresetId, workOrderDateRange, customDateRange]);

  const restorePersistedPreference = useCallback(() => {
    const { presetId, customDateRange: persistedCustomRange } = persistedPreferenceRef.current;
    setWorkOrderPresetId(presetId);
    setCustomDateRange(persistedCustomRange);
  }, []);

  const handleWorkOrderPresetChange = useCallback(
    async (newPresetId) => {
      const mutationId = presetMutationSeqRef.current + 1;
      presetMutationSeqRef.current = mutationId;

      setWorkOrderPresetId(newPresetId); // optimistic update → triggers refetch via effect
      if (newPresetId !== CUSTOM_WORK_ORDER_DASHBOARD_PRESET_ID) {
        setCustomDateRange(null); // clear stored custom bounds when switching to a fixed preset
      }

      const runPatch = async () => {
        try {
          await patchWorkOrderDashboardPreset(newPresetId);
          // Only the latest mutation may advance the persisted baseline.
          if (mutationId !== presetMutationSeqRef.current) return;
          persistedPreferenceRef.current = {
            presetId: newPresetId,
            customDateRange: newPresetId === CUSTOM_WORK_ORDER_DASHBOARD_PRESET_ID ? customDateRange : null
          };
        } catch (error) {
          // Ignore stale failures — a newer selection already owns the UI/server intent.
          if (mutationId !== presetMutationSeqRef.current) return;
          // Roll back to the last server-persisted preference (not an optimistic prior UI state).
          restorePersistedPreference();
          toast.error(translate('service_filter.save_error', { namespace }));
        }
      };

      // Serialize PATCHes so the server applies preference updates in selection order.
      const next = presetPatchChainRef.current.then(runPatch, runPatch);
      presetPatchChainRef.current = next.catch(() => {});
      await next;
    },
    [customDateRange, restorePersistedPreference]
  );

  const handleWorkOrderCustomApply = useCallback(
    async (range) => {
      // Always switch to custom preset and store the draft range (even if end is null).
      // Only PATCH + refetch when both bounds are present (partial range = user still picking).
      const mutationId = presetMutationSeqRef.current + 1;
      presetMutationSeqRef.current = mutationId;

      setWorkOrderPresetId(CUSTOM_WORK_ORDER_DASHBOARD_PRESET_ID);
      setCustomDateRange(range);

      if (!range.start || !range.end) {
        // Partial draft — do not PATCH or refetch yet.
        return;
      }

      const startDate = DateTime.fromJSDate(range.start).toISODate();
      const endDate = DateTime.fromJSDate(range.end).toISODate();

      const runPatch = async () => {
        try {
          await patchWorkOrderDashboardPreset(CUSTOM_WORK_ORDER_DASHBOARD_PRESET_ID, { startDate, endDate });
          if (mutationId !== presetMutationSeqRef.current) return;
          persistedPreferenceRef.current = {
            presetId: CUSTOM_WORK_ORDER_DASHBOARD_PRESET_ID,
            customDateRange: range
          };
        } catch (error) {
          if (mutationId !== presetMutationSeqRef.current) return;
          restorePersistedPreference();
          toast.error(translate('service_filter.save_error', { namespace }));
        }
      };

      const next = presetPatchChainRef.current.then(runPatch, runPatch);
      presetPatchChainRef.current = next.catch(() => {});
      await next;
    },
    [restorePersistedPreference]
  );

  const workOrderCountsLoading = isWorkOrderSummaryLoading && workOrderSummary === null;
  const workOrderRefreshing = isWorkOrderSummaryLoading && workOrderSummary !== null;

  if (loading || !dashboardData) {
    return (
      <div className="workspace__section">
        <div className="qmb-loading--96" data-title={I18n.t('generic.loading')}>
          <svg role="img">
            <use href="/map.svg#load-spinner" />
          </svg>
        </div>
      </div>
    );
  }

  return (
    <HomeDataTableProvider
      workOrderStatuses={workOrderSummary?.statuses ?? []}
      inspectionStatuses={dashboardData.inspectionStatuses}
      territoryFilter={territoryFilter}
      workOrderDateRange={workOrderDateRange}>
      <HomeDashboard
        dashboardData={dashboardData}
        territoryFilter={territoryFilter}
        onTerritoryFilterChange={setTerritoryFilter}
        workOrderPresetId={workOrderPresetId}
        workOrderCustomRange={customDateRange}
        onWorkOrderPresetChange={handleWorkOrderPresetChange}
        onWorkOrderCustomApply={handleWorkOrderCustomApply}
        workOrderRefreshing={workOrderRefreshing}
        workOrderCountsLoading={workOrderCountsLoading}
      />
    </HomeDataTableProvider>
  );
}

HomeDashboardWrapper.propTypes = {
  workOrderDatePreset: PropTypes.string,
  workOrderDateStart: PropTypes.string,
  workOrderDateEnd: PropTypes.string
};

HomeDashboardWrapper.defaultProps = {
  workOrderDatePreset: '',
  workOrderDateStart: '',
  workOrderDateEnd: ''
};

HomeDashboardWrapper.displayName = 'HomeDashboardWrapper';

export default HomeDashboardWrapper;
